import { calendarDataStatusOptions } from '@/constants';
import moment from 'moment';

/**
 * Returns string that should be used as a dictionary key when trying to key
 * values by date
 *
 * @param {moment} momentValue
 */
export function momentKeyFromMoment(momentValue) {
  const month = momentValue.month() + 1;
  const year = momentValue.year();
  return `${month}-${year}`;
}

/**
 * Returns a string build using moment.js that extracts month-year from the date given.
 * This is meant to be used when generating consistent dictionary keys based on date information,
 * regardless of the date string format
 *
 * @param {string} date date to be converted to key
 */
export function momentKeyFromDate(date) {
  const dateMoment = moment(date);
  return momentKeyFromMoment(dateMoment);
}

/**
 * Tells you the date number, isCheckin, bookDate, isBooked, rate from the index in dataPoints
 * where the date is `dayMoment`.
 *
 * @param {*} dayMoment
 * @param {*} lastTwoDataPoints
 */
export function getCalendarDayData(dayMoment, dataPointsKeyedByMoment) {
  const currDate = dayMoment.date();
  const currDateIndex = currDate - 1;

  const currMonthYearKey = momentKeyFromMoment(dayMoment);

  // This could be null. Calendar can render dates first few days of next month (may not have data)
  const currMonthDataPoint = dataPointsKeyedByMoment[currMonthYearKey];
  const bookingActivity = currMonthDataPoint?.calendarActivity[currDateIndex];
  const prevDayBookingActivity =
    currMonthDataPoint?.calendarActivity[currDateIndex - 1];
  const rate = currMonthDataPoint?.rate[currDateIndex];

  return {
    dateNumber: currDate,
    isCheckin: prevDayBookingActivity?.bbDate !== bookingActivity?.bbDate,
    bookDate: bookingActivity?.bbDate,
    status: bookingActivity?.status,
    rate,
  };
}

/**
 * Writes the ariaLabel to be used for DateCellWrapper
 * @param {String} status Rental status of the specified date
 * @param {String} date Booking date
 * @param {String} rate Booking rate (per night?)
 */
export function writeCalendarBookingAriaLabel(status, date, rate) {
  const { BOOKED, BLOCKED } = calendarDataStatusOptions;

  const bookingRate =
    typeof rate === 'number' && rate > 0 ? ` (Rate: $${rate})` : '';

  if (status === BLOCKED) {
    return `Blocked on ${date || 'unknown date'}`;
  } else if (status === BOOKED) {
    return `Booked on ${date || 'unknown date'}${bookingRate}`;
  }

  return bookingRate ? `Listed${bookingRate}` : null;
}
